import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { useState } from 'react';

import { AuthProvider } from '@/auth/AuthProvider';
import { FullPageMessage } from '@/components/FullPageMessage';
import { TooltipProvider } from '@/components/ui/tooltip';
import { configError } from '@/config';
import { AppRoutes } from '@/routes';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Per query `staleTime` is set from the source it reads (30 s rollups,
        // 0 live, 5 min cold, per plan 5.10). The default is deliberately zero
        // so a hook that forgets to set one refetches rather than silently
        // serving something old.
        staleTime: 0,
        gcTime: 5 * 60_000,
        // Focus refetch is off: this dashboard already polls where polling is
        // wanted, and a tab switch firing a batched Overview query is a cost
        // with no reader benefit.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: false,
      },
      mutations: { retry: false },
    },
  });
}

export function App() {
  // One client for the life of the document. Creating it in render without
  // state would reset every cache on each re-render.
  const [queryClient] = useState(createQueryClient);

  if (configError !== null) {
    return (
      <FullPageMessage
        tone="destructive"
        title="Zeligate is not configured"
        detail={
          <>
            <p>
              This build is missing {configError.missing.join(', ')}. The bundle cannot reach the
              API or the identity provider without them.
            </p>
            <p className="mt-2">
              Set them in the environment or in the hosting island configuration, then rebuild.
              See apps/dashboard/.env.example.
            </p>
          </>
        }
      />
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
